import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Switch,
    FormControlLabel,
    Typography,
    Box,
    Chip,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Snackbar,
    Alert,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { PhotoCamera, CheckCircle } from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { t } from "i18next";
import { api } from "common";

const ImageUploadBox = styled(Box)(({ theme }) => ({
    width: "100%",
    height: 200,
    border: `2px dashed ${theme.palette.primary.main}`,
    borderRadius: theme.shape.borderRadius,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    marginBottom: theme.spacing(2),
    position: "relative",
    "&:hover": {
        backgroundColor: theme.palette.action.hover,
    },
}));

const ImagePreview = styled("img")({
    width: "100%",
    height: "100%",
    objectFit: "cover",
    borderRadius: 8,
});

const CategoryChip = styled(Chip)(({ theme, selected }) => ({
    margin: theme.spacing(0.5),
    backgroundColor: selected ? theme.palette.primary.main : "transparent",
    color: selected
        ? theme.palette.primary.contrastText
        : theme.palette.text.primary,
    "&:hover": {
        backgroundColor: selected
            ? theme.palette.primary.dark
            : theme.palette.action.hover,
    },
}));

const EditCarDialog = ({ open, onClose, carData }) => {
    const dispatch = useDispatch();
    const { editCar } = api;
    const [loading, setLoading] = useState(false);
    const [selectedDriver, setSelectedDriver] = useState("");
    const [drivers, setDrivers] = useState([]);
    const [carImages, setCarImages] = useState([
        { image: null, label: "car_front" },
        { image: null, label: "car_driver_side" },
        { image: null, label: "car_back" },
        { image: null, label: "car_passenger_side" },
    ]);
    const [optionCars, setOptionCars] = useState([]);

    // Notification states
    const [notification, setNotification] = useState({
        open: false,
        message: "",
        severity: "info", // success, error, warning, info
    });

    // Success state untuk mengubah tampilan modal
    const [isSuccess, setIsSuccess] = useState(false);

    const cartypes = useSelector((state) => state.cartypes.cars);
    const categorylistdata = useSelector((state) => state.categorylistdata);
    const settings = useSelector((state) => state.settingsdata.settings);
    const featureFlags = useSelector((state) => state.featureflagsdata.data);
    const addons = useSelector((state) => state.addondata.data);
    const users = useSelector((state) => state.usersdata.users);
    const [isMultiService, setIsMultiService] = useState(false);

    const [formData, setFormData] = useState({
        vehicleMake: "",
        vehicleModel: "",
        vehicleNumber: "",
        other_info: "",
    });

    useEffect(() => {
        if (addons && addons.multi_service && settings) {
            setIsMultiService(true);
        }
    }, [featureFlags, addons, settings]);

    // Populate form data when carData changes
    useEffect(() => {
        if (carData && open) {
            console.log("🔄 EditCarDialog - Loading car data:", carData);
            console.log("🔍 carData.cars:", carData.cars);
            console.log("🔍 carData.carType:", carData.carType);
            console.log("🔍 carData.category:", carData.category);
            console.log("🔍 isMultiService:", isMultiService);

            setFormData({
                vehicleMake: carData.vehicleMake || "",
                vehicleModel: carData.vehicleModel || "",
                vehicleNumber: carData.vehicleNumber || "",
                other_info: carData.other_info || "",
            });

            // Set selected driver - use same logic as CarsListMultiService
            // In CarsListMultiService: drivers.find((d) => d.id === carGroup.driver)
            // carData.driverId is mapped from carGroup.driver, so we use driverId
            const driverUid = carData.driverId || carData.driver || "";
            console.log("🎯 Setting selectedDriver to:", driverUid);
            setSelectedDriver(driverUid);

            // Load car images
            if (carData.car_images && Array.isArray(carData.car_images)) {
                const imagesCopy = [...carImages];
                carData.car_images.forEach((img, index) => {
                    if (index < imagesCopy.length && img.image) {
                        imagesCopy[index] = { ...img };
                    }
                });
                setCarImages(imagesCopy);
            } else if (carData.image) {
                // Handle single image case
                const imagesCopy = [...carImages];
                imagesCopy[0] = { image: carData.image, label: "car_front" };
                setCarImages(imagesCopy);
            }

            // Set up option cars for both multi-service and single service
            if (carData.cars && Array.isArray(carData.cars)) {
                // Multi-service: use cars array
                setupOptionCarsFromExisting(carData.cars);
            } else if (carData.carType && carData.category) {
                // Single service: create array from carType and category
                const singleCarArray = [
                    {
                        carType: carData.carType,
                        category: carData.category,
                        selected: true,
                    },
                ];
                console.log(
                    "🔄 Setting up single service car:",
                    singleCarArray
                );
                setupOptionCarsFromExisting(singleCarArray);
            } else {
                // Fallback: set up empty option cars but initialize from category/type data
                console.log(
                    "🔄 No existing car data found, setting up default options"
                );
                // Call the regular setup to initialize option cars
                setupDefaultOptionCars();
            }
        }
    }, [carData, open]);

    const setupOptionCarsFromExisting = (existingCars) => {
        console.log("🔄 Setting up option cars from existing:", existingCars);
        console.log("🔍 categorylistdata:", categorylistdata);
        console.log("🔍 cartypes:", cartypes);

        let thisCarCategories = [];
        if (
            categorylistdata.categories &&
            categorylistdata.categories.length >= 1
        ) {
            let lst = categorylistdata.categories.filter((item) => item.active);
            console.log("🔍 Available categories:", lst);
            for (let i = 0; i < lst.length; i++) {
                thisCarCategories.push(lst[i]);
            }
        } else {
            console.log("❌ No categories found or categories length < 1");
        }

        let thisCarTypes = [];
        if (cartypes && cartypes.length >= 1) {
            let lst = cartypes.filter((item) => item.active);
            console.log("🔍 Available car types:", lst);
            for (let i = 0; i < lst.length; i++) {
                thisCarTypes.push(lst[i]);
            }
        } else {
            console.log("❌ No car types found or cartypes length < 1");
        }

        let thisOptionCars = [];
        if (thisCarCategories && thisCarCategories.length >= 1) {
            if (thisCarTypes && thisCarTypes.length >= 1) {
                console.log(
                    "🔄 Building option cars from categories and types"
                );
                for (let i = 0; i < thisCarCategories.length; i++) {
                    let data = thisCarCategories[i];
                    let types = [];

                    for (let n = 0; n < thisCarTypes.length; n++) {
                        let type = thisCarTypes[n];
                        if (type.category === data.category) {
                            // Check if this type is selected in existing cars
                            const isSelected = existingCars.some(
                                (car) =>
                                    car.carType === type.name &&
                                    car.category === data.category
                            );

                            console.log(
                                `🔍 Checking type ${type.name} in category ${data.category}: isSelected = ${isSelected}`
                            );
                            type.selected = isSelected;
                            types.push(type);
                        }
                    }

                    data.types = types;
                    // Check if any types in this category are selected
                    data.selected = types.some((type) => type.selected);
                    console.log(
                        `🔍 Category ${data.category} selected: ${data.selected}, types count: ${types.length}`
                    );
                    // Only add category if it has at least 1 active cartype
                    if (types.length >= 1) {
                        console.log(
                            `✅ Adding category ${data.category} (${types.length} active types)`
                        );
                        thisOptionCars.push(data);
                    } else {
                        console.log(
                            `❌ Skipping category ${data.category} (no active types)`
                        );
                    }
                }
            } else {
                console.log("❌ Cannot build option cars - missing car types");
            }
        } else {
            console.log("❌ Cannot build option cars - missing categories");
        }

        console.log("🔄 Final option cars setup:", thisOptionCars);
        console.log("🔄 Setting optionCars state with:", thisOptionCars);
        setOptionCars(thisOptionCars);
    };

    const setupDefaultOptionCars = () => {
        let thisCarCategories = [];
        if (
            categorylistdata.categories &&
            categorylistdata.categories.length >= 1
        ) {
            let lst = categorylistdata.categories.filter((item) => item.active);
            for (let i = 0; i < lst.length; i++) {
                thisCarCategories.push(lst[i]);
            }
        }

        let thisCarTypes = [];
        if (cartypes && cartypes.length >= 1) {
            let lst = cartypes.filter((item) => item.active);
            for (let i = 0; i < lst.length; i++) {
                thisCarTypes.push(lst[i]);
            }
        }

        let thisOptionCars = [];

        if (thisCarCategories && thisCarCategories.length >= 1) {
            if (thisCarTypes && thisCarTypes.length >= 1) {
                for (let i = 0; i < thisCarCategories.length; i++) {
                    let data = thisCarCategories[i];
                    let types = [];

                    for (let n = 0; n < thisCarTypes.length; n++) {
                        let type = thisCarTypes[n];
                        if (type.category === data.category) {
                            type.selected = false;
                            types.push(type);
                        }
                    }

                    data.types = types;
                    data.selected = false;
                    // Only add category if it has at least 1 active cartype
                    if (types.length >= 1) {
                        console.log(
                            `✅ [setupDefaultOptionCars] Adding category ${data.category} (${types.length} active types)`
                        );
                        thisOptionCars.push(data);
                    } else {
                        console.log(
                            `❌ [setupDefaultOptionCars] Skipping category ${data.category} (no active types)`
                        );
                    }
                }
            }
        }

        console.log("🔄 Setup default option cars:", thisOptionCars);
        setOptionCars(thisOptionCars);
    };

    useEffect(() => {
        if (users && users.length > 0) {
            const driversList = users
                .filter((user) => user.usertype === "driver")
                .map((driver) => ({
                    id: driver.id,
                    name: `${driver.firstName} ${driver.lastName}`,
                    email: driver.email,
                    uid: driver.uid,
                }));
            setDrivers(driversList);
        }
    }, [users]);

    useEffect(() => {
        // Only setup default options if no carData is being loaded
        if (!carData || !open) {
            setupDefaultOptionCars();
        }
    }, [
        categorylistdata.categories,
        cartypes,
        isMultiService,
        settings,
        carData,
        open,
    ]);

    const resetAllData = () => {
        setSelectedDriver("");
        setCarImages([
            { image: null, label: "car_front" },
            { image: null, label: "car_driver_side" },
            { image: null, label: "car_back" },
            { image: null, label: "car_passenger_side" },
        ]);
        setFormData({
            vehicleMake: "",
            vehicleModel: "",
            vehicleNumber: "",
            other_info: "",
        });
        setIsSuccess(false);
    };

    // Helper function to show notifications
    const showNotification = (message, severity = "info") => {
        setNotification({
            open: true,
            message,
            severity,
        });
    };

    const handleCloseNotification = () => {
        setNotification({
            ...notification,
            open: false,
        });
    };

    const handleClose = () => {
        resetAllData();
        onClose();
    };

    const handleImageUpload = async (event, imageIndex) => {
        const file = event.target.files[0];
        if (file) {
            const imageUrl = URL.createObjectURL(file);
            const newImages = [...carImages];
            newImages[imageIndex] = {
                ...newImages[imageIndex],
                image: imageUrl,
                file,
            };
            setCarImages(newImages);
        }
    };

    const uploadFile = async (file, storagePath) => {
        try {
            const storage = getStorage();
            const storageRef = ref(storage, storagePath);
            await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(storageRef);
            return downloadURL;
        } catch (error) {
            throw error;
        }
    };

    const isWebUrl = (url) => {
        return url?.startsWith("http://") || url?.startsWith("https://");
    };

    const handleToggleOption = (index, value) => {
        const newOptions = [...optionCars];

        if (!isMultiService && value === true) {
            newOptions.forEach((option, i) => {
                if (i !== index) {
                    option.selected = false;
                    option.types.forEach((type) => {
                        type.selected = false;
                    });
                }
            });
        }

        newOptions[index].selected = value;

        if (value === true) {
            const existingTypes = newOptions[index].types.filter(
                (type) => type.selected
            );
            if (
                existingTypes.length === 0 &&
                newOptions[index].types.length > 0
            ) {
                if (!isMultiService) {
                    newOptions[index].types[0].selected = true;
                    for (let i = 1; i < newOptions[index].types.length; i++) {
                        newOptions[index].types[i].selected = false;
                    }
                } else {
                    newOptions[index].types[0].selected = true;
                }
            }
        }

        if (value === false) {
            newOptions[index].types.forEach((type) => {
                type.selected = false;
            });
        }

        setOptionCars(newOptions);
    };

    const handleToggleChildOption = (parentIndex, childIndex, value) => {
        const parentSelected = optionCars[parentIndex].selected;
        if (!parentSelected) {
            showNotification(t("please_select_category"), "warning");
            return;
        }

        const isChildSelected =
            optionCars[parentIndex].types[childIndex].selected;
        const isChildNotSelected =
            optionCars[parentIndex].types.filter((type) => type.selected)
                .length === 1;

        if (isChildSelected && value === false && isChildNotSelected) {
            showNotification(t("at_least_one_type_selected"), "warning");
            return;
        }

        const newOptions = [...optionCars];

        if (!isMultiService && value === true) {
            newOptions[parentIndex].types.forEach((type, i) => {
                if (i !== childIndex) {
                    type.selected = false;
                }
            });

            newOptions.forEach((option, i) => {
                if (i !== parentIndex) {
                    option.selected = false;
                    option.types.forEach((type) => {
                        type.selected = false;
                    });
                }
            });
        }

        newOptions[parentIndex].types[childIndex].selected = value;
        setOptionCars(newOptions);
    };

    const handleSubmit = async () => {
        setLoading(true);
        showNotification(t("updating"), "info");
        try {
            // Validation - optimized with some() for early exit
            const missingImages = carImages.some((image) => !image.image);

            if (missingImages) {
                showNotification(t("please_add_images_for_car"), "warning");
                setLoading(false);
                return;
            }

            if (
                !formData.vehicleMake ||
                !formData.vehicleModel ||
                !formData.vehicleNumber ||
                !formData.other_info
            ) {
                showNotification(t("please_fill_all_fields"), "warning");
                setLoading(false);
                return;
            }

            if (!selectedDriver) {
                showNotification(t("please_select_driver"), "warning");
                setLoading(false);
                return;
            }

            // Prepare selected cars
            let thisSelectedCars = [];
            const thisOptionCars = optionCars.filter((item) => item.selected);

            if (thisOptionCars.length === 0) {
                showNotification(
                    t("please_select_at_least_one_category"),
                    "warning"
                );
                setLoading(false);
                return;
            }

            for (let i = 0; i < thisOptionCars.length; i++) {
                const types = thisOptionCars[i].types.filter(
                    (type) => type.selected
                );

                if (types.length === 0) {
                    showNotification(
                        t("please_select_at_least_one_car_type_for_category") +
                            " " +
                            t(thisOptionCars[i].category),
                        "warning"
                    );
                    setLoading(false);
                    return;
                }

                for (let j = 0; j < types.length; j++) {
                    const type = types[j];
                    // Preserve existing car data if available
                    const existingCar = carData.cars?.find(
                        (car) =>
                            car.carType === type.name &&
                            car.category === thisOptionCars[i].category
                    );

                    thisSelectedCars.push({
                        carType: type.name,
                        category: thisOptionCars[i].category,
                        approved: existingCar?.approved || false,
                        active: existingCar?.active || false,
                        enabled: existingCar?.enabled || false,
                        car_id: carData.car_id,
                        id: existingCar?.id || carData.id + "_" + type.name,
                    });
                }
            }

            if (thisSelectedCars.length === 0) {
                showNotification(
                    t("please_select_at_least_one_car_type"),
                    "warning"
                );
                setLoading(false);
                return;
            }

            // Show uploading message for user feedback
            if (carImages.some((img) => img.file)) {
                showNotification(t("uploading_images"), "info");
            }

            // Upload images in parallel for better performance
            const imageUploadPromises = carImages.map(async (image, index) => {
                let downloadURL = null;

                if (image.file) {
                    // New file to upload
                    const storagePath = `cars/${carData.car_id}_${image.label}`;
                    downloadURL = await uploadFile(image.file, storagePath);
                } else if (isWebUrl(image.image)) {
                    // Existing URL
                    downloadURL = image.image;
                }

                if (downloadURL) {
                    return {
                        image: downloadURL,
                        label: image.label,
                    };
                }
                return null;
            });

            // Wait for all uploads to complete
            const uploadResults = await Promise.all(imageUploadPromises);
            const newCarImages = uploadResults.filter(
                (result) => result !== null
            );

            // Prepare categories and carTypes
            const categories = [
                ...new Set(thisSelectedCars.map((c) => c.category)),
            ].join(",");
            const carTypes = [
                ...new Set(thisSelectedCars.map((c) => c.carType)),
            ].join(",");

            // Prepare updated car data
            const updatedCarData = {
                ...carData,
                active: carData.active || false,
                enabled: carData.enabled || false,
                cars: thisSelectedCars,
                car_images: newCarImages,
                driver: selectedDriver,
                other_info: formData.other_info,
                vehicleMake: formData.vehicleMake,
                vehicleModel: formData.vehicleModel,
                vehicleNumber: formData.vehicleNumber,
                category: categories,
                carType: carTypes,
                updatedAt: Date.now(),
            };

            // console.log("🔄 Updating car data:", updatedCarData);

            // Update via Redux action
            dispatch(editCar(updatedCarData, "Update"));

            // Set success state to change modal content
            setIsSuccess(true);
        } catch (error) {
            console.error("Error updating car:", error);
            showNotification(t("error_updating_car"), "error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { minHeight: "80vh" },
            }}
        >
            <DialogTitle>
                {isSuccess ? t("success") : t("edit_car")}
            </DialogTitle>
            <DialogContent>
                {isSuccess ? (
                    // Success content
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            py: 6,
                            textAlign: "center",
                        }}
                    >
                        <CheckCircle
                            sx={{
                                fontSize: 80,
                                color: "success.main",
                                mb: 3,
                            }}
                        />
                        <Typography
                            variant="h4"
                            sx={{
                                mb: 2,
                                color: "success.main",
                                fontWeight: "bold",
                            }}
                        >
                            {t("success")}!
                        </Typography>
                        <Typography
                            variant="h6"
                            sx={{ mb: 4, color: "text.secondary" }}
                        >
                            {t("car_updated_successfully")}
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{ color: "text.secondary", maxWidth: 400 }}
                        >
                            {t("car_data_has_been_updated_successfully")}
                        </Typography>
                    </Box>
                ) : (
                    // Regular edit form content
                    <Box sx={{ mt: 2 }}>
                        {/* Driver Information (Read-only saat edit) */}
                        <Box sx={{ mb: 3 }}>
                            <Typography
                                variant="subtitle1"
                                sx={{ mb: 1, fontWeight: "bold" }}
                            >
                                {t("driver")}
                            </Typography>
                            <TextField
                                fullWidth
                                value={(() => {
                                    if (!selectedDriver) {
                                        return t("no_driver_selected");
                                    }

                                    // Use same logic as CarsListMultiService: d.id === carGroup.driver
                                    const driver = drivers.find(
                                        (d) => d.id === selectedDriver
                                    );

                                    if (driver) {
                                        console.log(
                                            "🎯 Driver object structure:",
                                            driver
                                        );
                                    }

                                    return driver
                                        ? `${
                                              driver.desc ||
                                              driver.name ||
                                              driver.firstName +
                                                  " " +
                                                  driver.lastName ||
                                              "Unknown Driver"
                                          } (${driver.email || "No email"})`
                                        : `${t(
                                              "driver_not_found"
                                          )} (ID: ${selectedDriver})`;
                                })()}
                                InputProps={{
                                    readOnly: true,
                                }}
                                variant="outlined"
                                sx={{
                                    "& .MuiInputBase-input": {
                                        backgroundColor: "#f5f5f5",
                                        cursor: "default",
                                    },
                                }}
                            />
                            <Typography
                                variant="caption"
                                sx={{
                                    color: "text.secondary",
                                    mt: 1,
                                    display: "block",
                                }}
                            >
                                {t("driver_cannot_be_changed_during_edit")}
                            </Typography>
                        </Box>

                        {/* Car Images */}
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            {t("car_images")}
                        </Typography>
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            {carImages.map((image, index) => (
                                <Grid item xs={6} sm={3} key={index}>
                                    <Box>
                                        <Typography
                                            variant="caption"
                                            display="block"
                                            sx={{ mb: 1 }}
                                        >
                                            {t(image.label)}
                                        </Typography>
                                        <ImageUploadBox>
                                            {image.image ? (
                                                <ImagePreview
                                                    src={image.image}
                                                    alt={`Car ${index + 1}`}
                                                />
                                            ) : (
                                                <>
                                                    <PhotoCamera
                                                        sx={{
                                                            fontSize: 40,
                                                            color: "primary.main",
                                                            mb: 1,
                                                        }}
                                                    />
                                                    <Typography variant="body2">
                                                        {t("click_to_upload")}
                                                    </Typography>
                                                </>
                                            )}
                                            <input
                                                type="file"
                                                accept="image/*"
                                                style={{ display: "none" }}
                                                onChange={(e) =>
                                                    handleImageUpload(e, index)
                                                }
                                                id={`image-upload-${index}`}
                                            />
                                        </ImageUploadBox>
                                        <label
                                            htmlFor={`image-upload-${index}`}
                                        >
                                            <IconButton
                                                component="span"
                                                sx={{ width: "100%", mt: 1 }}
                                            >
                                                <PhotoCamera />
                                            </IconButton>
                                        </label>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>

                        {/* Multi-Service Categories and Types */}
                        {console.log(
                            "🎨 Render check - isMultiService:",
                            isMultiService,
                            "optionCars length:",
                            optionCars.length
                        )}
                        {isMultiService && (
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="h6" sx={{ mb: 2 }}>
                                    {t("select_categories_and_services")}
                                </Typography>
                                {console.log(
                                    "🎨 Rendering optionCars:",
                                    optionCars
                                )}
                                {optionCars.map((item, index) => (
                                    <Box
                                        key={index}
                                        sx={{
                                            mb: 2,
                                            p: 2,
                                            border: "1px solid #e0e0e0",
                                            borderRadius: 1,
                                        }}
                                    >
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={item.selected}
                                                    onChange={(e) =>
                                                        handleToggleOption(
                                                            index,
                                                            e.target.checked
                                                        )
                                                    }
                                                />
                                            }
                                            label={t(item.category)}
                                        />
                                        {item.types &&
                                            item.types.length > 0 && (
                                                <Box sx={{ mt: 1 }}>
                                                    {item.types.map(
                                                        (type, cIndex) => (
                                                            <CategoryChip
                                                                key={cIndex}
                                                                label={
                                                                    type.name
                                                                }
                                                                selected={
                                                                    type.selected
                                                                }
                                                                onClick={() =>
                                                                    handleToggleChildOption(
                                                                        index,
                                                                        cIndex,
                                                                        !type.selected
                                                                    )
                                                                }
                                                            />
                                                        )
                                                    )}
                                                </Box>
                                            )}
                                    </Box>
                                ))}
                            </Box>
                        )}

                        {/* Single Service Categories and Types */}
                        {console.log("🎨 Single service render check:", {
                            isMultiService,
                            optionCarsLength: optionCars.length,
                            showSection:
                                !isMultiService && optionCars.length > 0,
                        })}
                        {!isMultiService && (
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="h6" sx={{ mb: 2 }}>
                                    {t("select_single_service")}
                                </Typography>
                                {console.log(
                                    "🎨 Rendering single service optionCars:",
                                    optionCars
                                )}
                                {optionCars.length === 0 ? (
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: "text.secondary",
                                            fontStyle: "italic",
                                        }}
                                    >
                                        No categories available or loading...
                                    </Typography>
                                ) : (
                                    optionCars.map((item, index) => (
                                        <Box
                                            key={index}
                                            sx={{
                                                mb: 2,
                                                p: 2,
                                                border: "1px solid #e0e0e0",
                                                borderRadius: 1,
                                            }}
                                        >
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={item.selected}
                                                        onChange={(e) =>
                                                            handleToggleOption(
                                                                index,
                                                                e.target.checked
                                                            )
                                                        }
                                                    />
                                                }
                                                label={t(item.category)}
                                            />
                                            {item.types &&
                                                item.types.length > 0 && (
                                                    <Box sx={{ mt: 1 }}>
                                                        {item.types.map(
                                                            (type, cIndex) => (
                                                                <CategoryChip
                                                                    key={cIndex}
                                                                    label={
                                                                        type.name
                                                                    }
                                                                    selected={
                                                                        type.selected
                                                                    }
                                                                    onClick={() =>
                                                                        handleToggleChildOption(
                                                                            index,
                                                                            cIndex,
                                                                            !type.selected
                                                                        )
                                                                    }
                                                                />
                                                            )
                                                        )}
                                                    </Box>
                                                )}
                                        </Box>
                                    ))
                                )}
                            </Box>
                        )}

                        {/* Car Details */}
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            {t("car_details")}
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label={t("vehicle_model_name")}
                                    value={formData.vehicleMake}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            vehicleMake: e.target.value,
                                        })
                                    }
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label={t("vehicle_model_no")}
                                    value={formData.vehicleModel}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            vehicleModel: e.target.value,
                                        })
                                    }
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label={t("vehicle_reg_no")}
                                    value={formData.vehicleNumber}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            vehicleNumber: e.target.value,
                                        })
                                    }
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label={t("other_info")}
                                    value={formData.other_info}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            other_info: e.target.value,
                                        })
                                    }
                                    required
                                />
                            </Grid>
                        </Grid>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                {isSuccess ? (
                    // Success actions - only close button
                    <Button
                        onClick={handleClose}
                        variant="contained"
                        color="primary"
                        size="large"
                        sx={{ minWidth: 120 }}
                    >
                        {t("close")}
                    </Button>
                ) : (
                    // Regular form actions
                    <>
                        <Button onClick={handleClose} disabled={loading}>
                            {t("cancel")}
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            variant="contained"
                            disabled={loading}
                        >
                            {loading ? t("updating") : t("update")}
                        </Button>
                    </>
                )}
            </DialogActions>

            {/* Notification Snackbar */}
            <Snackbar
                open={notification.open}
                autoHideDuration={
                    notification.severity === "success" ? 8000 : 6000
                }
                onClose={handleCloseNotification}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                sx={{ zIndex: 9999 }}
            >
                <Alert
                    onClose={handleCloseNotification}
                    severity={notification.severity}
                    sx={{
                        width: "100%",
                        fontSize: "16px",
                        fontWeight:
                            notification.severity === "success"
                                ? "bold"
                                : "normal",
                    }}
                    variant={
                        notification.severity === "success"
                            ? "filled"
                            : "standard"
                    }
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </Dialog>
    );
};

export default EditCarDialog;
